/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	NgZone,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { TicketPlanApplication } from '@appPlanApplication/TicketPlanApplication';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { ContextMenuService } from 'src/app/modules/ticket-edit/context-menu.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { HoverOverStateService } from 'src/app/services/hover-over-state.service';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { ImportTicketsCommonService } from '../../import-tickets/services/import-tickets-common.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { CameraService } from '../../plan/services/camera.service';
import { LayerService } from '../../plan/services/layer.service';
import { TodayLineService } from '../../plan/services/today-line.service';
import { MilestoneLineService } from '../../plan/services/milestone-line.service';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { PullSpaceService } from '../../plan/services/pull-space.service';
import { ActiveLineService } from '../../plan/services/active-line.service';
import { ActiveColumnService } from '../../plan/services/active-column.service';
import { ProjectWorkingDayService } from '../../plan/services/project-working-day.service';
import { TimelineTicketService } from '../../plan/services/timeline-ticket.service';
import { TicketEditCommonDesktopService } from 'src/app/common/docked-ticket/services/ticket-edit-common-desktop.service';
import { FirebaseSnapshotsService } from 'src/app/services/firebase-snapshots.service';
import { PromisePeriodService } from '../../plan/services/promise-period.service';
import { PlanPrintService } from '../../plan/services/plan-print.service';
import { CrewSizeService } from '../../plan/services/crew-size.service';

@Component({
	selector: 'canvas-view',
	templateUrl: './canvas-view.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 */
export class CanvasViewComponent implements AfterViewInit, OnDestroy {
	private _defaultSettings;
	/**
	 * @member application
	 * @description Instance of the RendererApplication entry point
	 */
	public application: TicketPlanApplication;
	/**
	 * Get the canvas element. We need this to instantiate the renderer.
	 */
	@ViewChild('canvasView') public canvasView;
	/**
	 * @param {PlanState} planState
	 * @param contextMenuService
	 */
	constructor(
		private planState: PlanState,
		private contextMenuService: ContextMenuService,
		private ticketEditCommonService: TicketEditCommonDesktopService,
		private featureFlagManager: FeatureFlagManager,
		private hoverOverStateService: HoverOverStateService,
		private userPreferenceStateService: UserPreferenceStateService,
		private importTicketsCommonService: ImportTicketsCommonService,
		private planAccessCheck: PlanAccessCheck,
		private todayLineService: TodayLineService,
		private layerService: LayerService,
		private cameraService: CameraService,
		private milestoneLineService: MilestoneLineService,
		private activeLineService: ActiveLineService,
		private pullSpaceService: PullSpaceService,
		private activeColumnService: ActiveColumnService,
		private projectWorkingDayService: ProjectWorkingDayService,
		private timelineTicketService: TimelineTicketService,
		private firebaseSnapshotsService: FirebaseSnapshotsService,
		private promisePeriodService: PromisePeriodService,
		private planPrintService: PlanPrintService,
		private crewSizeService: CrewSizeService
	) {}

	public assembleAngularLand(): CanvasAngularLand {
		return {
			actions: this.planState.actions,
			legacyActions: this.planState.legacyActions,
			route: this.planState.route,
			tickets: this.planState.tickets,
			legacyTickets: this.planState.scopeSoup.tickets,
			ticketDragService: this.planState.ticketDragService,
			wideTicketSize$: this.planState.wideTicketSize$,
			renderQueueWithCleanup$: this.planState.renderQueueWithCleanup$,
			assignConnector: this.planState.assignCanvasConnector,
			debugRectangles: this.planState.debugRectangles,
			interactionBlocker: this.planState.interactionBlocker,
			floatingTickets: this.planState.floatingTickets,
			hud: this.planState.scopeSoup.hud,
			dependencies: this.planState.dependencies,
			userPreferenceStateService: this.userPreferenceStateService,
			contextMenuService: this.contextMenuService,
			ticketEditCommonService: this.ticketEditCommonService,
			featureFlagManager: this.featureFlagManager,
			hoverOverStateService: this.hoverOverStateService,
			importTicketsCommonService: this.importTicketsCommonService,
			planAccessCheck: this.planAccessCheck,
			stateSync: this.planState.stateSync,
			cycleTicket$: this.planState.cycleTicket$,
			planId: this.planState.planId,
			plan: this.planState.plan,
			plansApi: this.planState.plansApi,
			filterTransparencyValue$: this.planState.filterTransparencyValue$,
			swimlanes: this.planState.swimlanes,
			swimlaneDragService: this.planState.swimlaneDragService,
			canvasConnector: this.planState.canvasConnector, // TODO: Remove with TP-6774
			todayLineService: this.todayLineService,
			milestoneLineService: this.milestoneLineService,
			activeLineService: this.activeLineService,
			pullSpaceService: this.pullSpaceService,
			activeColumnService: this.activeColumnService,
			projectWorkingDayService: this.projectWorkingDayService,
			projectObject: this.planState.project,
			timelineTicketService: this.timelineTicketService,
			projectId: this.planState.projectId,
			firebaseSnapshotsService: this.firebaseSnapshotsService,
			// BEGIN PLACEHOLDER: Remove with TP-7117
			// We don't want PlanState on the CanvasAngularLand, however we need it still for old AngularJS services
			planState: this.planState,
			// END PLACEHOLDER
			cameraService: this.cameraService,
			promisePeriodService: this.promisePeriodService,
			planPrintService: this.planPrintService,
			crewSizeService: this.crewSizeService,
		};
	}

	/**
	 */
	public ngAfterViewInit() {
		// window.PIXI.settings.SCALE_MODE = window.PIXI.SCALE_MODES.NEAREST;
		// See the WebGL spec for specification of this: https://www.khronos.org/registry/webgl/specs/latest/1.0/ ctrl+f for 'powerPreference'
		/**
		 * Indicates a request for a GPU configuration that prioritizes rendering performance over power consumption.
		 * Any content that chooses this value should be aware that it is more likely that the user agent will force a lost context at any time.
		 * Implementations may decide to initially respect this request and, after some time, lose the context and restore a new context ignoring the request.
		 * Developers are encouraged to only specify this value if they believe it is absolutely necessary, since it may significantly decrease battery life on mobile devices.
		 * - default
		 * - high-performance (dual GPU)
		 * - low-power (conserve battery life)
		 */
		this._defaultSettings = {
			width: window.innerWidth,
			height: window.innerHeight,
			antialias:
				this.userPreferenceStateService.planDisableAliasing !== undefined
					? !this.userPreferenceStateService.planDisableAliasing
					: true,
			resolution: window.devicePixelRatio && window.devicePixelRatio >= 1 ? window.devicePixelRatio : 1,
			autoResize: false,
			forceCanvas: this.shouldForceCanvas(),
			backgroundAlpha: 0,
			hello: true,
			shouldInitializeGestureContainer: true,
			gestureContainerLayer: LayerPaths.BACKGROUND_GESTURES,
		};

		const unsub = this.planState.stateChange$.subscribe((stateEvent: StateEvents) => {
			if (stateEvent > 0 && stateEvent < 100 && stateEvent !== StateEvents.CANVAS_LOADED) {
				this.startRenderer();
			}

			if (stateEvent === StateEvents.BOOMED) {
				this.application.destroy();
				this.application = null;
				(<any>window).TouchPlanCanvas = null;
				unsub.unsubscribe();
			}
		});

		// MOC-3285 - Disable right click on canvas (So the quick status menu is not covered by normal context menu
		this.canvasView.nativeElement.oncontextmenu = (e) => e.preventDefault();
	}
	/**
	 */
	public startRenderer() {
		const localSettings = this._defaultSettings;
		localSettings['view'] = <HTMLCanvasElement>this.canvasView.nativeElement;
		localSettings['forceCanvas'] = this._defaultSettings.forceCanvas;
		localSettings['antialias'] = this._defaultSettings.antialias;
		const canvasAngularLand = this.assembleAngularLand();

		this.application = new TicketPlanApplication(
			canvasAngularLand,
			localSettings,
			this.userPreferenceStateService
		);

		(<any>window).TouchPlanCanvas = this.application;
		this.planState.assignCanvasConnector(this.application);
		this.cameraService.registerPlanData(this.application.camera);
		this.layerService.registerPlanData(this.application.layerManager);
	}

	public ngOnDestroy() {
		(<any>window).TouchPlanCanvas = null;
		(<any>window).CanvasDebug = null;
		this.cameraService.destroy();
	}

	private shouldForceCanvas(): boolean {
		if (this.userPreferenceStateService.forceCanvasRenderer !== null) {
			return this.userPreferenceStateService.forceCanvasRenderer;
		}

		if (this.userPreferenceStateService.disableWebGL !== undefined) {
			return this.userPreferenceStateService.disableWebGL;
		}

		return this.featureFlagManager.getFlag(DynamicFlagTypes.WEB_GL_ENABLED);
	}
}
